import { Trash2 } from 'lucide-react'
import { Fragment } from 'react'

import { Button } from '@/shared/components/ui/button'
import { FieldError } from '@/shared/components/ui/field-error'
import { Input } from '@/shared/components/ui/input'
import { cn } from '@/shared/lib/utils'

const EXPORT_TAX_TYPES = ['export_lut_igst', 'export_lut_igst_sgst_cgst']

const countWords = (text = '') => {
  return String(text).trim().split(/\s+/).filter(Boolean).length
}

export default function ProductRow({
  product,
  index,
  errors = {},
  onChange,
  onRemove,
  taxType = 'cgst_sgst',
}) {
  const fieldError = (field) => errors[`products.${index}.${field}`]

  const fieldClass = (field) =>
    cn(fieldError(field) && 'border-red-500 focus-visible:ring-red-500')

  const isExportSupply = EXPORT_TAX_TYPES.includes(taxType)
  const wordCount = countWords(product.description || '')

  const handleDescriptionChange = (e) => {
    const val = e.target.value
    const words = val.trim().split(/\s+/).filter(Boolean)
    if (words.length > 500) {
      const trimmed = val.trim().split(/\s+/).slice(0, 500).join(' ')
      onChange(index, 'description', trimmed)
      return
    }
    onChange(index, 'description', val)
  }

  return (
    <Fragment>
      <tr className="border-b-0 align-top">
        {/* Product */}
        <td className="min-w-[160px] p-2">
          <Input
            placeholder="Product"
            value={product.product}
            maxLength={100}
            onChange={(e) =>
              onChange(
                index,
                'product',
                e.target.value
                  .replace(/[^a-zA-Z0-9\s.,'()-]/g, '')
                  .slice(0, 100),
              )
            }
            aria-invalid={Boolean(fieldError('product'))}
            data-invalid={fieldError('product') ? 'true' : undefined}
            className={fieldClass('product')}
          />

          <FieldError message={fieldError('product')} />
        </td>

        {/* HSN */}
        <td className="min-w-[100px] p-2">
          <Input
            placeholder="HSN"
            value={product.hsn}
            onChange={(e) => onChange(index, 'hsn', e.target.value)}
          />
        </td>

        {/* Unit Price */}
        <td className="min-w-[110px] p-2">
          <Input
            type="number"
            placeholder="0"
            value={product.unitPrice}
            onChange={(e) => onChange(index, 'unitPrice', e.target.value)}
            aria-invalid={Boolean(fieldError('unitPrice'))}
            data-invalid={fieldError('unitPrice') ? 'true' : undefined}
            className={fieldClass('unitPrice')}
          />

          <FieldError message={fieldError('unitPrice')} />
        </td>

        {/* Quantity */}
        <td className="min-w-[90px] p-2">
          <Input
            type="number"
            placeholder="1"
            value={product.quantity}
            onChange={(e) => onChange(index, 'quantity', e.target.value)}
            aria-invalid={Boolean(fieldError('quantity'))}
            data-invalid={fieldError('quantity') ? 'true' : undefined}
            className={fieldClass('quantity')}
          />

          <FieldError message={fieldError('quantity')} />
        </td>

        {/* Before GST */}
        <td className="min-w-[110px] p-2">
          <Input value={product.beforeGST} disabled />
        </td>

        {/* Tax */}
        {taxType === 'cgst_sgst' ? (
          <>
            {/* CGST */}
            <td className="min-w-[90px] p-2">
              <Input
                value={product.cgst}
                onChange={(e) => onChange(index, 'cgst', e.target.value)}
                aria-invalid={Boolean(fieldError('cgst'))}
                data-invalid={fieldError('cgst') ? 'true' : undefined}
                className={fieldClass('cgst')}
              />

              <FieldError message={fieldError('cgst')} />
            </td>

            {/* SGST */}
            <td className="min-w-[90px] p-2">
              <Input
                value={product.sgst}
                onChange={(e) => onChange(index, 'sgst', e.target.value)}
                aria-invalid={Boolean(fieldError('sgst'))}
                data-invalid={fieldError('sgst') ? 'true' : undefined}
                className={fieldClass('sgst')}
              />

              <FieldError message={fieldError('sgst')} />
            </td>
          </>
        ) : isExportSupply ? (
          /* Export / SEZ Supply */
          <td className="min-w-[90px] p-2">
            <Input value="0" disabled className="bg-gray-100 text-gray-600" />
          </td>
        ) : (
          /* IGST */
          <td className="min-w-[90px] p-2">
            <Input
              value={product.igst}
              onChange={(e) => onChange(index, 'igst', e.target.value)}
              aria-invalid={Boolean(fieldError('igst'))}
              data-invalid={fieldError('igst') ? 'true' : undefined}
              className={fieldClass('igst')}
            />

            <FieldError message={fieldError('igst')} />
          </td>
        )}

        {/* Total */}
        <td className="min-w-[110px] p-2">
          <Input value={product.total} disabled />
        </td>

        {/* Action */}
        <td className="p-2 text-center">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() => onRemove(index)}
            title="Remove product"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </td>
      </tr>

      {/* Product Description Input Box */}
      <tr className="border-b bg-slate-50/40">
        <td
          colSpan={taxType === 'cgst_sgst' ? 9 : 8}
          className="px-3 pb-3 pt-1"
        >
          <div className="rounded-xl border border-slate-200/80 bg-white p-2.5 shadow-2xs">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Product Description (Optional — Max 500 words)
              </span>
              <span
                className={cn(
                  'text-[11px] font-medium',
                  wordCount > 500 ? 'text-red-500 font-bold' : 'text-slate-400',
                )}
              >
                {wordCount} / 500 words
              </span>
            </div>
            <textarea
              rows={2}
              placeholder="Enter product description, technical specifications, or scope of work (up to 500 words)..."
              value={product.description || ''}
              onChange={handleDescriptionChange}
              className={cn(
                'w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 transition focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500',
                fieldError('description') &&
                  'border-red-500 focus:ring-red-500',
              )}
            />
            <FieldError message={fieldError('description')} />
          </div>
        </td>
      </tr>
    </Fragment>
  )
}
